import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { VesselsAPI, Vessel, VesselPayload, VesselCategory } from '../services/vessels.ts';
import api from '../services/api.ts';
import { parseCurrency, formatCurrencyInput } from '../utils/currency.ts';
import { ArrowLeftIcon, DocumentTextIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import BoatIcon from '../components/icons/BoatIcon.tsx';
import FileUpload from '../components/FileUpload.tsx';
import FormPage from '../components/ui/FormPage.tsx';

const EditVessel: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [vessel, setVessel] = useState<Vessel | null>(null);
  const [form, setForm] = useState<Partial<VesselPayload>>({});
  const [purchasePriceDisplay, setPurchasePriceDisplay] = useState<string>('');
  const [sellingPriceDisplay, setSellingPriceDisplay] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const data = await VesselsAPI.get(Number(id));
        setVessel(data);
        setForm({
          make: data.make,
          model: data.model,
          year: data.year,
          registrationNumber: data.registrationNumber,
          color: data.color,
          purchasePrice: Number(data.purchasePrice),
          sellingPrice: Number(data.sellingPrice),
          purchaseDate: data.purchaseDate?.split('T')[0],
          category: data.category,
          attachments: data.attachments || [],
        });
        setPurchasePriceDisplay(formatCurrencyInput(Number(data.purchasePrice || 0)));
        setSellingPriceDisplay(
          data.sellingPrice != null && Number(data.sellingPrice) > 0
            ? formatCurrencyInput(Number(data.sellingPrice))
            : ''
        );
      } catch (err) {
        console.error('Erro ao carregar embarcação:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const updateMutation = useMutation(
    async () => {
      if (!id) return;

      let uploadedFiles = form.attachments || [];
      const newFiles = uploadedFiles.filter((file: any) => file.file instanceof File);
      const existingFiles = uploadedFiles.filter((file: any) => !(file.file instanceof File));

      if (newFiles.length > 0) {
        const formData = new FormData();
        newFiles.forEach((attachment: any) => {
          formData.append('files', attachment.file);
        });

        const uploadResponse = await api.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (uploadResponse.data.success) {
          const newUploadedFiles = uploadResponse.data.data.files.map((file: any, index: number) => ({
            id: Date.now() + index,
            name: file.originalName,
            filename: file.filename,
            url: file.url,
            size: file.size,
            mimetype: file.mimetype,
            type: newFiles[index].type,
            category: newFiles[index].category,
          }));
          uploadedFiles = [...existingFiles, ...newUploadedFiles];
        }
      } else {
        uploadedFiles = existingFiles;
      }

      const purchasePrice = parseCurrency(purchasePriceDisplay);
      const sellingPrice = parseCurrency(sellingPriceDisplay);
      const payload: Partial<VesselPayload> = {
        ...form,
        purchasePrice,
        sellingPrice: sellingPrice > 0 ? sellingPrice : null,
        attachments: uploadedFiles,
      } as any;
      return VesselsAPI.update(Number(id), payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vessels']);
        queryClient.invalidateQueries(['vessel', id]);
        navigate(`/vessels/${id}`);
      },
      onError: err => {
        console.error('Erro ao atualizar embarcação:', err);
      },
    }
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === 'year' ? Number(value) : value,
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleCurrencyChange = (field: 'purchasePrice' | 'sellingPrice', raw: string) => {
    if (field === 'purchasePrice') setPurchasePriceDisplay(raw);
    else setSellingPriceDisplay(raw);
    const numeric = parseCurrency(raw);
    setForm(prev => ({ ...prev, [field]: numeric }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const blurCurrency = (field: 'purchasePrice' | 'sellingPrice') => {
    const raw = field === 'purchasePrice' ? purchasePriceDisplay : sellingPriceDisplay;
    const numeric = parseCurrency(raw);
    const pretty = raw.trim() ? formatCurrencyInput(numeric) : '';
    if (field === 'purchasePrice') setPurchasePriceDisplay(pretty);
    else setSellingPriceDisplay(pretty);
    setForm(prev => ({ ...prev, [field]: numeric }));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.make?.trim()) next.make = 'Marca é obrigatória';
    if (!form.model?.trim()) next.model = 'Modelo é obrigatório';
    if (!form.registrationNumber?.trim()) next.registrationNumber = 'Registro é obrigatório';
    if (!form.color?.trim()) next.color = 'Cor é obrigatória';
    if (!form.year || Number(form.year) < 1900) next.year = 'Ano inválido';
    if (!form.purchaseDate) next.purchaseDate = 'Data de compra é obrigatória';
    if (!form.category) next.category = 'Categoria é obrigatória';
    if (!form.purchasePrice || Number(form.purchasePrice) <= 0) next.purchasePrice = 'Valor de compra deve ser maior que zero';
    if (form.sellingPrice !== undefined && Number(form.sellingPrice) < 0) next.sellingPrice = 'Valor de venda não pode ser negativo';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      updateMutation.mutate();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!vessel) {
    return <div className="p-8 text-center text-red-500">Embarcação não encontrada.</div>;
  }

    return (
    <FormPage
      backTo="/vessels"
      backLabel="Voltar às embarcações"
      breadcrumb="Patrimônio / Embarcações / Editar"
      title="Editar embarcação"
      subtitle="Atualize os dados da embarcação"
    >
<form id="edit-vessel-form" onSubmit={handleSubmit} className="space-y-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-600" />
              Informações Básicas
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Marca *</label>
                <input
                  name="make"
                  value={form.make || ''}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.make ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.make && <p className="mt-1 text-sm text-red-600">{errors.make}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Modelo *</label>
                <input
                  name="model"
                  value={form.model || ''}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.model ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.model && <p className="mt-1 text-sm text-red-600">{errors.model}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ano *</label>
                <input
                  type="number"
                  name="year"
                  value={form.year || new Date().getFullYear()}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.year ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.year && <p className="mt-1 text-sm text-red-600">{errors.year}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Registro *</label>
                <input
                  name="registrationNumber"
                  value={form.registrationNumber || ''}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.registrationNumber ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.registrationNumber && <p className="mt-1 text-sm text-red-600">{errors.registrationNumber}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cor *</label>
                <input
                  name="color"
                  value={form.color || ''}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.color ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.color && <p className="mt-1 text-sm text-red-600">{errors.color}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoria *</label>
                <select
                  name="category"
                  value={(form.category || 'boat') as VesselCategory}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.category ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="boat">Barco</option>
                  <option value="yacht">Iate</option>
                  <option value="sailboat">Veleiro</option>
                  <option value="other">Outro</option>
                </select>
                {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data de Compra *</label>
                <input
                  type="date"
                  name="purchaseDate"
                  value={form.purchaseDate || new Date().toISOString().split('T')[0]}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.purchaseDate ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.purchaseDate && <p className="mt-1 text-sm text-red-600">{errors.purchaseDate}</p>}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <CurrencyDollarIcon className="h-6 w-6 mr-2 text-primary-600" />
              Valores
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor de Compra *</label>
                <input
                  type="text"
                  name="purchasePrice"
                  value={purchasePriceDisplay}
                  onChange={e => handleCurrencyChange('purchasePrice', e.target.value)} onBlur={() => blurCurrency('purchasePrice')}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.purchasePrice ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="R$ 0,00"
                  inputMode="decimal"
                />
                {errors.purchasePrice && <p className="mt-1 text-sm text-red-600">{errors.purchasePrice}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor de Venda (estimado)</label>
                <input
                  type="text"
                  name="sellingPrice"
                  value={sellingPriceDisplay}
                  onChange={e => handleCurrencyChange('sellingPrice', e.target.value)} onBlur={() => blurCurrency('sellingPrice')}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.sellingPrice ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="R$ 0,00"
                  inputMode="decimal"
                />
                {errors.sellingPrice && <p className="mt-1 text-sm text-red-600">{errors.sellingPrice}</p>}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Fotos e Documentos</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Adicione fotos da embarcação e documentos como registro, licença, etc.
            </p>
            <FileUpload
              files={(form.attachments as any[]) || []}
              onFilesChange={files => setForm(prev => ({ ...prev, attachments: files }))}
              maxFiles={20}
              maxSizePerFile={10}
              showCategories={true}
            />
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(`/vessels/${id}`)}
              className="btn btn-secondary"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={updateMutation.isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-60"
            >
              {updateMutation.isLoading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
          </FormPage>
  );
};

export default EditVessel;